import { create } from 'zustand';
import apiClient from '../lib/api';

/**
 * People ↔ Assets bridge (/assets/employees → people.employees + holdings, departments, offices).
 * Used by Asset Hub pickers (employee details when assigning) and by the
 * People hub (an employee's assets, licence seats and offboarding recovery).
 * Actions throw an Error with the server's message on failure.
 */

export interface AssetEmployee {
  id: string;
  code: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  department: string | null;
  designation: string | null;
  location: string | null;
  status: string | null;
  employmentType: string | null;
  joinDate: string | null;
  userId: string | null;
  accountStatus: string | null;
  managerId: string | null;
  managerName: string | null;
  assetCount: number;
  licenseCount: number;
}

export interface AssetDepartment {
  id: string;
  name: string;
  description: string | null;
}

export interface AssetOffice {
  id: string;
  name: string;
  code: string | null;
  locationType: string | null;
  city: string | null;
  address: string | null;
}

export interface HeldAsset {
  id: string;
  assetCode: string;
  name: string;
  category: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  condition: string | null;
  purchaseCost: number | null;
  warrantyEndDate: string | null;
  assignedDate: string | null;
}

export interface HeldLicense {
  assignmentId: string;
  licenseId: string;
  softwareName: string;
  publisher: string | null;
  licenseType: string | null;
  expiryDate: string | null;
  assignedDate: string;
  deviceCode: string | null;
}

export interface EmployeeHoldings {
  employee: AssetEmployee;
  assets: HeldAsset[];
  licenses: HeldLicense[];
  requests: { id: string; assetType: string; priority: string; status: string; createdAt: string }[];
  history: { id: string; action: string; details: string | null; createdAt: string; performedByName: string | null; assetCode: string | null }[];
}

export const mapEmployee = (e: any): AssetEmployee => ({
  id: e.id,
  code: e.employee_code,
  name: e.name || e.email || 'Unnamed employee',
  email: e.email,
  phone: e.phone,
  department: e.department,
  designation: e.designation,
  location: e.location || null,
  status: e.status,
  employmentType: e.employment_type,
  joinDate: e.join_date,
  userId: e.user_id,
  accountStatus: e.account_status,
  managerId: e.manager_id,
  managerName: e.manager_name,
  assetCount: Number(e.asset_count) || 0,
  licenseCount: Number(e.license_count) || 0,
});

const mapHoldings = (h: any): EmployeeHoldings => ({
  employee: mapEmployee(h.employee),
  assets: (h.assets || []).map((a: any) => ({
    id: a.id, assetCode: a.asset_code, name: a.name, category: a.category, manufacturer: a.manufacturer,
    model: a.model, serialNumber: a.serial_number, condition: a.condition, purchaseCost: a.purchase_cost,
    warrantyEndDate: a.warranty_end_date, assignedDate: a.assigned_date,
  })),
  licenses: (h.licenses || []).map((l: any) => ({
    assignmentId: l.assignment_id, licenseId: l.license_id, softwareName: l.software_name, publisher: l.publisher,
    licenseType: l.license_type, expiryDate: l.expiry_date, assignedDate: l.assigned_date, deviceCode: l.device_code,
  })),
  requests: (h.requests || []).map((r: any) => ({
    id: r.id, assetType: r.asset_type, priority: r.priority, status: r.status, createdAt: r.created_at,
  })),
  history: (h.history || []).map((x: any) => ({
    id: x.id, action: x.action, details: x.details, createdAt: x.created_at,
    performedByName: x.performed_by_name, assetCode: x.asset_code,
  })),
});

const dataOf = (res: any) => res.data?.data ?? res.data;
const errorOf = (e: any, fallback: string) => new Error(e?.response?.data?.message || e?.message || fallback);

interface EmployeeAssetsState {
  directory: AssetEmployee[];
  directoryLoaded: boolean;
  directoryError: string | null;

  departments: AssetDepartment[];
  departmentsLoaded: boolean;
  offices: AssetOffice[];
  officesLoaded: boolean;

  fetchDirectory: (opts?: { includeInactive?: boolean }) => Promise<AssetEmployee[]>;
  fetchDepartments: () => Promise<AssetDepartment[]>;
  fetchOffices: () => Promise<AssetOffice[]>;
  fetchHoldings: (employeeId: string) => Promise<EmployeeHoldings>;
  fetchMyHoldings: () => Promise<EmployeeHoldings>;
  recover: (employeeId: string, body?: { condition?: string; notes?: string }) =>
    Promise<{ returnedAssets: string[]; releasedLicenses: string[]; holdings: EmployeeHoldings }>;
}

export const useEmployeeAssetsStore = create<EmployeeAssetsState>((set, get) => ({
  directory: [],
  directoryLoaded: false,
  directoryError: null,

  departments: [],
  departmentsLoaded: false,
  offices: [],
  officesLoaded: false,

  fetchDirectory: async (opts) => {
    try {
      const res = await apiClient.get('/assets/employees', { params: opts?.includeInactive ? { includeInactive: 'true' } : undefined });
      const directory = (dataOf(res) || []).map(mapEmployee);
      set({ directory, directoryLoaded: true, directoryError: null });
      return directory;
    } catch (e) {
      const err = errorOf(e, 'Could not load employees');
      set({ directoryLoaded: true, directoryError: err.message });
      throw err;
    }
  },

  fetchDepartments: async () => {
    try {
      const res = await apiClient.get('/assets/employees/departments');
      const rows = dataOf(res) || [];
      const departments: AssetDepartment[] = rows.map((d: any) => ({
        id: String(d.id),
        name: d.name,
        description: d.description || null,
      }));
      set({ departments, departmentsLoaded: true });
      return departments;
    } catch (e) {
      console.warn('Could not load departments', e);
      return [];
    }
  },

  fetchOffices: async () => {
    try {
      const res = await apiClient.get('/assets/employees/offices');
      const rows = dataOf(res) || [];
      const offices: AssetOffice[] = rows.map((o: any) => ({
        id: String(o.id),
        name: o.name,
        code: o.code || null,
        locationType: o.location_type || null,
        city: o.city || null,
        address: o.address || null,
      }));
      set({ offices, officesLoaded: true });
      return offices;
    } catch (e) {
      console.warn('Could not load offices', e);
      return [];
    }
  },

  fetchHoldings: async (employeeId) => {
    try {
      const holdings = mapHoldings(dataOf(await apiClient.get(`/assets/employees/${employeeId}`)));
      // Keep the directory's counts in step with what was just read.
      set({ directory: get().directory.map((e) => (e.id === employeeId ? holdings.employee : e)) });
      return holdings;
    } catch (e) { throw errorOf(e, 'Could not load employee assets'); }
  },

  fetchMyHoldings: async () => {
    try { return mapHoldings(dataOf(await apiClient.get('/assets/employees/me'))); }
    catch (e) { throw errorOf(e, 'Could not load your assets'); }
  },

  recover: async (employeeId, body) => {
    try {
      const d = dataOf(await apiClient.post(`/assets/employees/${employeeId}/recover`, body || {}));
      const holdings = mapHoldings(d.holdings);
      set({ directory: get().directory.map((e) => (e.id === employeeId ? holdings.employee : e)) });
      return { returnedAssets: d.returnedAssets || [], releasedLicenses: d.releasedLicenses || [], holdings };
    } catch (e) { throw errorOf(e, 'Could not recover assets'); }
  },
}));
